// src/pages/admin/Transactions.js
import React, { useEffect, useState, useRef } from 'react';
import { Search, Loader2, RefreshCw, BellRing, Clock, CheckCircle, XCircle, AlertTriangle, List, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { notifyTransactionChange } from '../../utils/transactionNotifications';

import TransactionsTable from '../../components/admin/TransactionsTable';
import TransactionModal from '../../components/admin/TransactionModal';

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('todas'); 
  const [selectedTx, setSelectedTx] = useState(null); 
  
  // 🔥 Estados para las notificaciones
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioContextRef = useRef(null);

  // ===========================
  // 0. Configuración de Notificaciones Nativas
  // ===========================
  useEffect(() => {
    // Pedir permiso al navegador cuando carga la página
    if ("Notification" in window) {
      if (Notification.permission === "granted") {
        setNotificationsEnabled(true);
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(permission => {
          if (permission === "granted") {
            setNotificationsEnabled(true);
          }
        });
      }
    }
  }, []);

  // Función para reproducir un sonido sutil tipo "Ding"
  const playNotificationSound = () => {
    if (!soundEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // Nota A5
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1); // Baja a A4 rápido

      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05); // Volumen suave (0.3)
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {
      console.warn("No se pudo reproducir el sonido:", e);
    }
  };

  // 🔥 Función para disparar la notificación visual (CORREGIDA)
  const triggerNotification = (title, body) => {
    playNotificationSound();
    
    if (notificationsEnabled && "Notification" in window) {
      if (Notification.permission === "granted") {
        new Notification(title, {
          body: body,
          icon: '/notification-icon.png',
          badge: '/notification-badge-v2.png',
        });
      }
    }
  };

  // ===========================
  // 1. Carga Inicial
  // ===========================
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(`*, payment_methods_origin (is_push)`)
        .order('created_at', { ascending: false });

      if (!error) setTransactions(data || []);
    } catch (err) {
      console.error("Error fetching:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, []);

  // ===========================
  // 2. REALTIME INTELIGENTE
  // ===========================
  const fetchSingleTx = async (id) => {
    const { data } = await supabase
      .from('transactions')
      .select(`*, payment_methods_origin (is_push)`)
      .eq('id', id)
      .single();
    return data;
  };

  useEffect(() => {
    const channel = supabase.channel('tx_realtime_smart')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'transactions' }, async (payload) => {
        const newTx = await fetchSingleTx(payload.new.id);
        if (newTx) {
          setTransactions(prev => [newTx, ...prev]);
          
          // 🔥 NOTIFICAR NUEVA ORDEN CREADA
          triggerNotification(
            "💰 Nueva Orden Recibida", 
            `Orden #${newTx.id} por ${newTx.amount} ${newTx.origin_currency}.`
          );
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'transactions' }, async (payload) => {
        const updatedTx = await fetchSingleTx(payload.new.id);
        if (updatedTx) {
          setTransactions(prev => {
            const oldTx = prev.find(t => t.id === updatedTx.id);
            
            if (oldTx) {
              // 1. 🚀 NOTIFICACIÓN PUSH (CLIENTE EN PASO 5)
              if (oldTx.current_step !== 'confirm' && updatedTx.current_step === 'confirm' && updatedTx.payment_methods_origin?.is_push) {
                 
                 // Aviso Visual para Admin
                 triggerNotification(
                   "🔔 ¡Atención! Cobro PUSH Requerido", 
                   `Cliente esperando cobro de tu app para la orden #${updatedTx.id}.`
                 );

                 notifyTransactionChange(updatedTx, oldTx).catch(err =>
                   console.error('Error notificando PUSH al usuario:', err)
                 );
              }
              
              // 2. NOTIFICACIÓN TRADICIONAL: El cliente sube la referencia y pasa a "Procesando"
              if (oldTx.status !== 'Procesando' && updatedTx.status === 'Procesando' && !updatedTx.payment_methods_origin?.is_push) {
                 triggerNotification(
                   "✅ Pago Reportado", 
                   `Revisa la orden #${updatedTx.id}. El cliente ya subió la referencia de pago.`
                 );
              }
            }

            return prev.map(t => (t.id === updatedTx.id ? updatedTx : t));
          });
          
          setSelectedTx(prev => (prev?.id === updatedTx.id ? updatedTx : prev));
        }
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'transactions' }, (payload) => {
        setTransactions(prev => prev.filter(t => t.id !== payload.old.id));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [notificationsEnabled, soundEnabled]); 

  // ===========================
  // 3. Lógica de Filtrado
  // ===========================
  const getFilteredTransactions = () => {
    let filteredByTab = transactions;

    switch (activeTab) {
      case 'push':
        filteredByTab = transactions.filter(t => 
          t.current_step === 'confirm' && 
          t.payment_methods_origin?.is_push &&
          !['Procesando', 'Completado', 'Cancelada'].includes(t.status)
        );
        break;
      case 'proceso':
        filteredByTab = transactions.filter(t => t.status === 'Procesando');
        break;
      case 'completadas':
        filteredByTab = transactions.filter(t => t.status === 'Completado');
        break;
      case 'canceladas':
        filteredByTab = transactions.filter(t => t.status === 'Cancelada');
        break;
      case 'incompletas':
        filteredByTab = transactions.filter(t => ['Creando', 'En pago', 'en pago', 'Método seleccionado'].includes(t.status));
        break;
      default:
        filteredByTab = transactions; 
    }

    if (!search.trim()) return filteredByTab;

    const q = search.toLowerCase();
    return filteredByTab.filter(t => 
      String(t.id).includes(q) || 
      String(t.origin_country || '').toLowerCase().includes(q) || 
      String(t.payment_reference || '').toLowerCase().includes(q) ||
      String(t.user_name || '').toLowerCase().includes(q) 
    );
  };

  const finalTransactions = getFilteredTransactions();

  const pendingPushCount = transactions.filter(t => 
    t.current_step === 'confirm' && 
    t.payment_methods_origin?.is_push &&
    !['Procesando', 'Completado', 'Cancelada'].includes(t.status)
  ).length;

  const tabs = [
    { id: 'todas', label: 'Todas', icon: List, color: 'text-slate-600' },
    { id: 'push', label: 'Pendiente PUSH', icon: BellRing, color: 'text-red-600', count: pendingPushCount },
    { id: 'proceso', label: 'En Proceso', icon: Clock, color: 'text-yellow-600' },
    { id: 'completadas', label: 'Completadas', icon: CheckCircle, color: 'text-green-600' },
    { id: 'canceladas', label: 'Canceladas', icon: XCircle, color: 'text-gray-500' },
    { id: 'incompletas', label: 'Abandonadas', icon: AlertTriangle, color: 'text-orange-500' },
  ];

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Gestión de Transacciones</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-slate-500 font-medium">Administra y procesa los envíos.</p>
            
            <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-1.5 rounded-lg transition-colors ${soundEnabled ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:bg-slate-100'}`}
                title={soundEnabled ? "Silenciar alertas" : "Activar sonido"}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              
              {!notificationsEnabled && "Notification" in window && (
                 <button 
                   onClick={() => Notification.requestPermission().then(p => setNotificationsEnabled(p === "granted"))}
                   className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded uppercase tracking-wider"
                 >
                   Activar Alertas Web
                 </button>
              )}
            </div>
          </div>
        </div>
        
        <button onClick={fetchTransactions} className="flex items-center justify-center gap-2 bg-white border border-slate-200 shadow-sm px-4 py-2.5 rounded-xl hover:bg-slate-50 font-bold text-slate-600 text-sm transition-all active:scale-95 w-full md:w-auto">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </button>
      </div>

      <div className="flex overflow-x-auto hide-scrollbar gap-2 mb-6 pb-2 border-b border-slate-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl font-bold text-sm transition-all whitespace-nowrap border-b-2
                ${isActive ? `bg-white ${tab.color} border-[currentColor] shadow-[0_-4px_10px_rgba(0,0,0,0.02)]` : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-50 hover:text-slate-700'}
              `}
            >
              <Icon className="w-4 h-4" /> 
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full animate-pulse">
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-6 bg-white p-3.5 rounded-2xl shadow-sm border border-slate-100">  
        <Search className="w-5 h-5 text-slate-400 ml-2" />  
        <input  
          value={search}  
          onChange={(e) => setSearch(e.target.value)}  
          placeholder="Buscar por ID de orden, país, usuario o referencia..."  
          className="outline-none w-full text-slate-700 placeholder-slate-400 font-medium text-[15px]"  
        />  
      </div>  

      {loading && transactions.length === 0 ? (
        <div className="flex justify-center py-20">  
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />  
        </div> 
      ) : (
        <TransactionsTable 
          transactions={finalTransactions} 
          onOpenDetails={(t) => setSelectedTx(t)} 
          onRefresh={() => {}} 
        />
      )}

      {selectedTx && (
        <TransactionModal 
          txId={selectedTx.id} 
          onClose={() => setSelectedTx(null)} 
          onRefresh={fetchTransactions} 
        />
      )}

    </div>
  );
}

